import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import type { DetailChecklistEintrag } from '@/types/detail';

/**
 * Die Kacheln einer Checkliste — an der Bestellung wie an der Sendung
 * (A58, #143).
 *
 * WARUM EIN GEMEINSAMES BAUTEIL UND KEIN ZWEITES DANEBEN
 *   Mit der Sendungs-Checkliste gäbe es dieselbe Kachel zweimal:
 *   dieselben Farben, dieselbe Mindesthöhe, dieselbe Unterscheidung
 *   zwischen „anklickbar" und „ergibt sich". Genau diese Verdopplung
 *   bezahlen wir gerade an anderer Stelle — WegeAbschnitt und
 *   ListenAbschnitt tun dasselbe zweimal, und in A54 musste dieselbe
 *   Korrektur zweimal gemacht werden, weil A43 nur eine Hälfte kannte
 *   (Fallstrick 15).
 *
 * WARUM NICHT DIE GANZE ChecklistSektion WIEDERVERWENDET WIRD
 *   Weil sie mehr ist als die Kacheln: eine Überschrift
 *   („Bearbeitungsstand"), der Satz über die drei berechneten Punkte
 *   der Bestellung, und der Benachrichtigungs-Zusatz aus A56. Nichts
 *   davon gehört an eine Sendung. Sie mitzuschleppen hieße, sie mit
 *   Schaltern für Fälle auszustatten, die es nur an einer der beiden
 *   Stellen gibt — und ein Bauteil mit vier „nur hier"-Schaltern ist
 *   wieder zwei Bauteile, nur unübersichtlicher.
 *
 *   Geteilt wird deshalb das, was WIRKLICH dasselbe ist: das Gitter.
 *   Was drumherum steht, bringt jede Seite selbst mit.
 */
export function ChecklistGitter({
  eintraege,
  hinweis,
  onToggle,
  zusatzSymbol
}: {
  eintraege: DetailChecklistEintrag[];
  /** Ein Satz unter den berechneten Kacheln — was sie ableitet. */
  hinweis?: string;
  onToggle: (code: string, wert: boolean) => void;
  /** Optionales Zeichen am Ende einer bestimmten Kachel (A56). */
  zusatzSymbol?: (code: string) => ReactNode;
}) {
  if (eintraege.length === 0) return null;

  // Berechnete Punkte folgen aus Daten und werden nur angezeigt.
  // Manuelle bleiben klickbar (B14).
  const berechnet = eintraege.filter((c) => c.berechnet);
  const manuell = eintraege.filter((c) => !c.berechnet);

  return (
    <>
      {berechnet.length > 0 && (
        <div className="mb-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {berechnet.map((c) => (
              <div
                key={c.code}
                className={clsx(
                  'flex min-h-[44px] items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm',
                  c.erledigt
                    ? 'border-teal-light bg-teal-soft text-teal'
                    : 'border-line bg-cream-warm text-ink-faint'
                )}
                title="Ergibt sich aus dem Bearbeitungsstand"
              >
                <span
                  aria-hidden
                  className={clsx(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    c.erledigt ? 'border-teal bg-teal text-cream' : 'border-line'
                  )}
                >
                  {c.erledigt && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0">{c.label}</span>
                <span className="sr-only">
                  {c.erledigt ? ' — erledigt' : ' — offen'} (automatisch ermittelt)
                </span>
              </div>
            ))}
          </div>
          {hinweis !== undefined && (
            <p className="mt-1 text-xs text-ink-faint">{hinweis}</p>
          )}
        </div>
      )}

      {manuell.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {manuell.map((c) => (
            <button
              key={c.code}
              type="button"
              aria-pressed={c.erledigt}
              onClick={() => onToggle(c.code, !c.erledigt)}
              className={clsx(
                'flex min-h-[44px] items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                c.erledigt
                  ? 'border-teal bg-teal-soft text-teal'
                  : 'border-line bg-cream text-ink-soft hover:border-teal hover:text-teal'
              )}
            >
              <span
                aria-hidden
                className={clsx(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                  c.erledigt ? 'border-teal bg-teal text-cream' : 'border-line'
                )}
              >
                {c.erledigt && <Check className="h-3 w-3" />}
              </span>
              <span className="min-w-0">{c.label}</span>
              {zusatzSymbol?.(c.code)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
